import React, { useEffect, useState } from "react";
import { Loader2, Lock, LockOpen, CalendarClock, FileText, HandCoins, Banknote, PiggyBank, Landmark, ClipboardList, Ship, FileDown, Users, FileMinus2, TrendingUp, Target, Scale } from "lucide-react";
import api, { formatApiError, money, openPdf } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";

const MONTHS = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const periodLabel = (p) => { const [y, m] = (p || "").split("-"); return `${MONTHS[parseInt(m, 10)] || m} ${y}`; };
const totalsStr = (t) => { const e = Object.entries(t || {}); return e.length ? e.map(([c, v]) => money(c, v)).join(" · ") : money("GTQ", 0); };

function Metric({ icon: Icon, label, value, sub }) {
  return (
    <Card className="rounded-sm border shadow-none p-4 space-y-1" data-testid={`closing-metric-${label}`}>
      <div className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-widest">{label}</span></div>
      <div className="text-lg font-heading font-bold">{value}</div>
      {sub != null && <div className="text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function ResCell({ label, value, sub, tone }) {
  return (
    <div className="bg-card p-3" data-testid={`closing-res-${label}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-1 text-lg font-heading font-bold ${tone || ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function Closing() {
  const { can, user } = useAuth();
  const canClose = can("closing:close");
  const isAdmin = user?.is_admin;
  const [periods, setPeriods] = useState([]);
  const [current, setCurrent] = useState("");
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);

  const loadList = async (keep) => {
    try {
      const { data } = await api.get("/closing");
      setPeriods(data.periods); setCurrent(data.current_period);
      const sel = keep || data.periods[0]?.period || data.current_period;
      setSelected(sel);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadDetail = async (p) => {
    if (!p) return;
    setLoading(true);
    try { const { data } = await api.get(`/closing/${p}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadList(); }, []);
  useEffect(() => { loadDetail(selected); }, [selected]);

  const doClose = async () => {
    try { await api.post(`/closing/${selected}/close`, { note }); toast.success(`Período ${periodLabel(selected)} cerrado`); setConfirmClose(false); setNote(""); await loadList(selected); loadDetail(selected); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doReopen = async () => {
    try { await api.post(`/closing/${selected}/reopen`); toast.success(`Período reabierto`); setConfirmReopen(false); await loadList(selected); loadDetail(selected); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const s = detail?.summary;
  const isClosed = detail?.closed;
  const isFuture = selected > current;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div className="space-y-1.5 w-full sm:w-72">
          <Label className="text-xs font-bold uppercase tracking-widest">Período</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="rounded-sm" data-testid="closing-period-select"><SelectValue placeholder="Selecciona un mes" /></SelectTrigger>
            <SelectContent>{periods.map((p) => <SelectItem key={p.period} value={p.period}>{periodLabel(p.period)}{p.closed ? " · Cerrado" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          {isClosed ? <Badge className="rounded-sm bg-red-100 text-red-700 gap-1.5 px-3 py-1.5" data-testid="closing-status-badge"><Lock className="h-3.5 w-3.5" /> Cerrado</Badge>
            : <Badge className="rounded-sm bg-green-100 text-green-700 gap-1.5 px-3 py-1.5" data-testid="closing-status-badge"><LockOpen className="h-3.5 w-3.5" /> Abierto</Badge>}
          {s && can("closing:export") && <Button variant="outline" className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs" onClick={() => openPdf(`/closing/${selected}/pdf`)} data-testid="closing-pdf-btn"><FileDown className="h-4 w-4" /> PDF</Button>}
          {canClose && !isClosed && !isFuture && <Button className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs" onClick={() => setConfirmClose(true)} data-testid="close-period-btn"><Lock className="h-4 w-4" /> Cerrar período</Button>}
          {isAdmin && isClosed && <Button variant="outline" className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs" onClick={() => setConfirmReopen(true)} data-testid="reopen-period-btn"><LockOpen className="h-4 w-4" /> Reabrir</Button>}
        </div>
      </div>

      {isClosed && <Card className="rounded-sm border-l-4 border-l-primary shadow-none p-3 flex items-center gap-2 text-sm bg-muted/40" data-testid="closing-locked-info">
        <CalendarClock className="h-4 w-4 text-primary" />
        <span>Cerrado por <strong>{detail.closed_by_name}</strong> el {new Date(detail.closed_at).toLocaleString("es-GT")}. Los documentos de este mes están bloqueados. {detail.note && <em>· {detail.note}</em>}</span>
      </Card>}

      {loading || !s ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="closing-summary">
            <Metric icon={FileText} label="Facturación" value={totalsStr(s.invoicing.totals)} sub={`${s.invoicing.count} facturas${s.invoicing.voided ? ` · ${s.invoicing.voided} anuladas` : ""}`} />
            <Metric icon={HandCoins} label="Cobros" value={totalsStr(s.collected.totals)} sub={`${s.collected.count} recibos`} />
            <Metric icon={Banknote} label="Pagos a proveedores" value={totalsStr(s.supplier_payments.totals)} sub={`${s.supplier_payments.count} pagos`} />
            <Metric icon={PiggyBank} label="Caja chica" value={money("GTQ", s.petty_cash.total)} sub={`${s.petty_cash.count} gastos`} />
            <Metric icon={Landmark} label="Tesorería" value={`+${money("GTQ", s.treasury.ingresos)}`} sub={`Egresos ${money("GTQ", s.treasury.egresos)} · ${s.treasury.movements} mov.`} />
            <Metric icon={ClipboardList} label="Compras" value={totalsStr(s.purchases.totals)} sub={`${s.purchases.count} órdenes`} />
            <Metric icon={Ship} label="Importaciones" value={totalsStr(s.imports.totals)} sub={`${s.imports.count} importaciones`} />
            <Metric icon={Users} label="Nómina" value={money("GTQ", s.payroll?.total || 0)} sub={`${s.payroll?.count || 0} comprobantes`} />
            <Metric icon={FileMinus2} label="Notas de crédito" value={money("GTQ", s.credit_notes?.total || 0)} sub={`${s.credit_notes?.count || 0} notas`} />
          </div>

          {s.resultado && (
            <Card className="rounded-sm border shadow-none overflow-hidden" data-testid="closing-resultado">
              <div className="p-4 border-b border-border flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="font-heading font-bold text-sm">Resultado del mes</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border">
                <ResCell label="Ventas" value={money("GTQ", s.resultado.venta_total)} />
                <ResCell label="Costo de ventas" value={money("GTQ", s.resultado.costo_total)} />
                <ResCell label="Utilidad bruta" value={money("GTQ", s.resultado.utilidad_bruta)} sub={`Margen ${s.resultado.margen_bruto}%`} tone="text-emerald-600" />
                <ResCell label="Gastos fijos" value={money("GTQ", s.resultado.gastos_fijos)} />
                <ResCell label="Gastos variables" value={money("GTQ", s.resultado.gastos_variables)} />
                <ResCell label="Utilidad neta" value={money("GTQ", s.resultado.utilidad_neta)} sub={`Margen ${s.resultado.margen_neto}%`} tone={s.resultado.utilidad_neta >= 0 ? "text-emerald-600" : "text-destructive"} />
              </div>
              <div className="p-4 border-t border-border flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
                <div className="flex items-center gap-2"><Target className="h-4 w-4 text-primary" /><span className="font-bold">Punto de equilibrio:</span> <span className="font-heading font-bold text-primary">{money("GTQ", s.resultado.punto_equilibrio)}</span></div>
                <div><span className="text-muted-foreground">Margen de contribución:</span> <strong>{money("GTQ", s.resultado.margen_contribucion)}</strong> ({s.resultado.margen_contribucion_pct}%)</div>
              </div>
            </Card>
          )}

          {s.balances && (
            <Card className="rounded-sm border shadow-none overflow-hidden" data-testid="closing-balances">
              <div className="p-4 border-b border-border flex items-center gap-2">
                <Scale className="h-4 w-4 text-primary" />
                <span className="font-heading font-bold text-sm">Saldos al cierre</span>
                <span className="text-xs text-muted-foreground">Foto actual</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-border">
                <ResCell label="Cuentas por cobrar" value={money("GTQ", s.balances.cxc)} tone="text-emerald-700" />
                <ResCell label="Cuentas por pagar" value={money("GTQ", s.balances.cxp)} tone="text-destructive" />
                <ResCell label="Bancos / caja" value={money("GTQ", s.balances.bank_cash)} />
                <ResCell label="Deuda tarjetas" value={money("GTQ", s.balances.card_debt)} tone="text-destructive" />
                <ResCell label="Caja chica" value={money("GTQ", s.balances.petty_balance)} />
              </div>
            </Card>
          )}
        </>
      )}

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>¿Cerrar {periodLabel(selected)}?</AlertDialogTitle>
            <AlertDialogDescription>Una vez cerrado, no se podrán registrar, editar ni anular documentos financieros de este mes (facturas, recibos, pagos, caja chica, tesorería). Solo un administrador podrá reabrirlo.</AlertDialogDescription></AlertDialogHeader>
          <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Nota (opcional)</Label>
            <Textarea className="rounded-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observaciones del cierre..." data-testid="closing-note" /></div>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel><AlertDialogAction className="rounded-sm font-bold" onClick={doClose} data-testid="confirm-close-btn">Cerrar período</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmReopen} onOpenChange={setConfirmReopen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>¿Reabrir {periodLabel(selected)}?</AlertDialogTitle>
            <AlertDialogDescription>Se desbloqueará el período y volverá a permitir cambios en sus documentos.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel><AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={doReopen} data-testid="confirm-reopen-btn">Reabrir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
